import { __awaiter } from "tslib";
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from '@shared/routing';
import MediaQuery from 'react-responsive';
import { TravellerFieldEnum, useOrderQuery, useSaveOrderServicesMutation, useFfpCompleteOrderMutation, OrderStatus } from '@websky/graphql';
import { getUserValue, MOBILE_MIN_WIDTH } from '../../../../../../../utils';
import { StepType } from '../../../../../../types';
import Toolbar from '../../../../../../../Toolbar';
import { useTheme } from '../../../../../../../theme';
import { isFormLocked } from '../../../../../../store/formIsLocked/selectors';
import { FooterMobile } from '@shared/ui';
import { getBonusForFlight, getIsBooked, getIsBookedOrConfirmed, getIsCancelled, getIsConfirmedOrCancelled, getIsContactsInfoIsEmpty, getIsFareLockSelected, getIsNew, getIsTravellersDataFilled, getOrder as getRootOrder, getOrderId, getPaymentIsRequired, getPriceInMiles, getSelectedInsuranceCodes, getServicesFallbackURL, getTotalInsurancePrice } from '../../../../../../store/order/selectors';
import { Money as MoneyComponent } from '../../../../../../../Money';
import { useUpsaleWarning } from '../../../../Upsale/hooks';
import Warning from '../../../../Upsale/Warning/Warning';
import { getTotalPriceToPay } from '../../../../../../store/orderPrice/selectors';
import PassengersToolbarCheckbox from '../PassengersToolbarCheckbox/PassengersToolbarCheckbox';
import { useCustomerContactsForm } from '../../../../../../../CustomerContactsForm/hooks';
import { fillOrder } from '../../../../../../store/order/actions';
import CustomerContactsModal from '../../../../../../../CustomerContactsForm/Modal/CustomerContactsModal';
import { getPassengerState } from '../../../../../../store/passengers/selectors';
import { NULL_COMPONENT, useRenderers } from '../../../../../../../renderProps';
import { InsuranceUpsaleModal } from '../../../../../../../InsurancePage';
import { useInsuranceUpsaleModal } from '../../../../../../../InsurancePage/components/InsuranceUpsaleModal/InsuranceUpsaleModal.hook';
import { saveOrderServicesParamsFactory } from '../../../../../../../InsurancePage/store/utils';
import AIPurchasePredictionService from '../../../../../../../AIPurchasePredictionService/components/AIPurchasePredictionService';
import { setMultipleSelectedServices } from '../../../../../../store/selectedServices/actions';
import { useAiPurchasePredictionService } from '../../../../../../../AIPurchasePredictionService/hook';
import { isServicesSaving } from '../../../../../../store/servicesIsSaving/selectors';
import { WEBSKY_SERVICE_OPEN_EVENT } from '../../../../../../constants';
import WebskyAnalyticsApollo from '../../../../../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { PurchaseShowPopupAction, WxEvents } from '../../../../../../../DataLayerAnalytics/types';
import { getAiPurchasePopupGoal } from '../../../../../../../DataLayerAnalytics/utils';
import SimpleLoader from '../../../../../../../SimpleLoader';
import InsuranceModalConfirmINN from '../../../../../../../InsuranceModalConfirmINN';
import { usePassengersToConfirmIIN } from '../../../../../../../InsuranceModalConfirmINN/hooks';
import { isGdsServiceType } from '../../../../../../../AIPurchasePredictionService/utils';
const PassengersToolbarWithoutForm = ({ formRef, currentStep, setNextStep, disableBooking, loading, formState, onConfirmConsents, showConsents = true, updateTravellers }) => {
    const { t } = useTranslation('Checkout');
    const { CheckoutStyles: checkoutTheme, PassengersToolbar: toolbarTheme } = useTheme('Checkout');
    const dispatch = useDispatch();
    const history = useHistory();
    const isNew = useSelector(getIsNew);
    const isBookedOrConfirmed = useSelector(getIsBookedOrConfirmed);
    const isConfirmedOrCancelled = useSelector(getIsConfirmedOrCancelled);
    const isBooked = useSelector(getIsBooked);
    const formIsLocked = useSelector(isFormLocked);
    const totalInsurancePrice = useSelector(getTotalInsurancePrice);
    const totalPriceToPay = useSelector(getTotalPriceToPay);
    const servicesRedirectURL = useSelector(getServicesFallbackURL);
    const { toggleWarningIfOrderConfirmed, showWarning, closeWarning } = useUpsaleWarning();
    const isOrderCancelled = useSelector(getIsCancelled);
    const bonusForFlight = useSelector(getBonusForFlight);
    const priceInMilesForFlight = useSelector(getPriceInMiles);
    const isPaymentRequired = useSelector(getPaymentIsRequired);
    const isTravellersDataFilled = useSelector(getIsTravellersDataFilled);
    const isFareLockSelected = useSelector(getIsFareLockSelected);
    const isContactsInfoEmpty = useSelector(getIsContactsInfoIsEmpty);
    const passengersValues = useSelector(getPassengerState);
    const order = useSelector(getRootOrder);
    const orderId = useSelector(getOrderId);
    const selectedInsuranceCodes = useSelector(getSelectedInsuranceCodes);
    const isOrderServicesSaving = useSelector(isServicesSaving);
    let handleSubmit;
    if (!!(updateTravellers === null || updateTravellers === void 0 ? void 0 : updateTravellers.valid)) {
        handleSubmit = updateTravellers.handleSubmit;
    }
    const [servicesLoading, setServicesLoading] = useState(false);
    const { refetch: getOrder } = useOrderQuery({ skip: true });
    const [saveOrderServices, saveOrderServicesResult] = useSaveOrderServicesMutation();
    const [ffpCompleteOrder, ffpCompleteOrderResult] = useFfpCompleteOrderMutation();
    const { saveClientInfo, closeContactsModal, openContactsModal, isContactsModalOpen, loading: isClientInfoSaving } = useCustomerContactsForm();
    const insuranceUpsaleModal = useInsuranceUpsaleModal({ strategy: 'checkout-before-payment' });
    const aiPurchasePredictionService = useAiPurchasePredictionService();
    const { renderPassengersToolbarAdditional: RenderPassengersToolbarAdditionalOverride = NULL_COMPONENT } = useRenderers();
    const { passengersToConfirmIIN, isNeededToConfirmIIN, closeInsuranceModal, openInsuranceModal, isInsuranceModalOpen, isSaveOrderServicesLoading, saveInsuranceAndUpdateOrder } = usePassengersToConfirmIIN(setNextStep);
    const goToPayment = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (handleSubmit) {
            const values = passengersValues.map(traveller => {
                var _a;
                return ({
                    id: traveller.id,
                    values: (_a = traveller.values) === null || _a === void 0 ? void 0 : _a.map(value => ({
                        type: value.type,
                        name: value.name,
                        value: value.value
                    }))
                });
            });
            yield handleSubmit(values);
            if (!isTravellersDataFilled) {
                return;
            }
        }
        else if (formRef === null || formRef === void 0 ? void 0 : formRef.current) {
            formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
            return;
        }
        if (showWarning) {
            return toggleWarningIfOrderConfirmed();
        }
        if (isPaymentRequired && (isContactsInfoEmpty || isNeededToConfirmIIN)) {
            if (isContactsInfoEmpty) {
                openContactsModal();
            }
            else if (isNeededToConfirmIIN) {
                openInsuranceModal();
            }
            return;
        }
        if (order.ffpInfo.skipPayment) {
            const result = yield ffpCompleteOrder({ variables: { id: orderId } });
            const ffpCompleteOrderResult = (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.FfpCompleteOrder;
            if ((ffpCompleteOrderResult === null || ffpCompleteOrderResult === void 0 ? void 0 : ffpCompleteOrderResult.result) && (ffpCompleteOrderResult === null || ffpCompleteOrderResult === void 0 ? void 0 : ffpCompleteOrderResult.order)) {
                dispatch(fillOrder(ffpCompleteOrderResult.order));
            }
        }
        else {
            setNextStep();
        }
    }), [
        dispatch,
        handleSubmit,
        passengersValues,
        showWarning,
        isPaymentRequired,
        isContactsInfoEmpty,
        isTravellersDataFilled,
        setNextStep,
        order.ffpInfo.skipPayment
    ]);
    const onContinuePurchasePredictionService = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (onConfirmConsents) {
            onConfirmConsents();
            return;
        }
        setServicesLoading(true);
        yield aiPurchasePredictionService.onContinue(currentStep.type, goToPayment);
        setServicesLoading(false);
    }), [onConfirmConsents, goToPayment, aiPurchasePredictionService.onContinue]);
    const saveClientInfoAndUpdateOrder = (data) => __awaiter(void 0, void 0, void 0, function* () {
        yield saveClientInfo({ variables: Object.assign(Object.assign({}, data), { orderId }) });
        const { data: getOrderResult } = yield getOrder({ id: orderId });
        if (getOrderResult === null || getOrderResult === void 0 ? void 0 : getOrderResult.Order) {
            dispatch(fillOrder(getOrderResult.Order));
        }
        closeContactsModal();
        if (isNeededToConfirmIIN) {
            openInsuranceModal();
        }
        else {
            setNextStep();
        }
    });
    const savePriorityInsurance = useCallback((selectedCodes = []) => __awaiter(void 0, void 0, void 0, function* () {
        const selectedCodesSet = new Set([...selectedInsuranceCodes, ...selectedCodes]);
        if (!selectedCodesSet.size) {
            return;
        }
        const travellerIds = order.travellers.map(traveller => traveller.id);
        const saveOrderParams = saveOrderServicesParamsFactory(travellerIds, [...selectedCodesSet]);
        const { data } = yield saveOrderServices({
            variables: {
                params: Object.assign(Object.assign({}, saveOrderParams.params), { id: order.id })
            }
        });
        if (data === null || data === void 0 ? void 0 : data.SaveOrderServices) {
            dispatch(fillOrder(data.SaveOrderServices));
        }
        yield onContinuePurchasePredictionService();
    }), [order, selectedInsuranceCodes, onContinuePurchasePredictionService, dispatch]);
    const saveAdditionalService = (service) => __awaiter(void 0, void 0, void 0, function* () {
        dispatch(setMultipleSelectedServices([
            {
                passengerId: service.passengerId,
                allowedSegments: service.allowedSegments,
                segmentId: service.segmentId,
                serviceId: service.serviceId,
                count: service.count,
                service: {
                    id: service.service.id,
                    type: service.service.type
                }
            }
        ], { custom_ecommerce_name: getAiPurchasePopupGoal(service.service.type, PurchaseShowPopupAction.Add) }));
    });
    useEffect(() => {
        if (!isOrderServicesSaving && aiPurchasePredictionService.isAddedService) {
            goToPayment();
        }
    }, [isOrderServicesSaving, aiPurchasePredictionService.isAddedService]);
    useEffect(() => {
        if (aiPurchasePredictionService.isOpen) {
            WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.AiPurchaseModalOpen, {
                servicesIds: [aiPurchasePredictionService.service.id]
            });
        }
    }, [aiPurchasePredictionService.isOpen]);
    const onChangeStep = useCallback((service) => {
        const ecommerceName = isGdsServiceType(service.type)
            ? getAiPurchasePopupGoal(service.type, PurchaseShowPopupAction.Change)
            : null;
        const event = new CustomEvent(WEBSKY_SERVICE_OPEN_EVENT, {
            detail: {
                service,
                callback: goToPayment,
                meta: { custom_ecommerce_name: ecommerceName }
            }
        });
        document.dispatchEvent(event);
    }, [goToPayment]);
    const onAiPurchaseDisagree = () => {
        WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.AiPurchaseModalClose, {
            servicesIds: [aiPurchasePredictionService.service.id]
        });
        aiPurchasePredictionService.onDisagree(goToPayment);
    };
    const customerValues = React.useMemo(() => {
        const customer = order.customer;
        return {
            email: getUserValue(customer, TravellerFieldEnum.Email),
            phone: getUserValue(customer, TravellerFieldEnum.Phone)
        };
    }, [order.customer]);
    const goBack = useCallback(() => {
        history.goBack();
    }, [history]);
    const hasPriceOrMilesToPay = totalPriceToPay.amount > 0 || (order.ffpInfo.skipPayment && order.status !== OrderStatus.Confirmed);
    const showToolbar = (!formIsLocked && (currentStep === null || currentStep === void 0 ? void 0 : currentStep.type) !== StepType.Payment && !isOrderCancelled && hasPriceOrMilesToPay) ||
        !isTravellersDataFilled;
    const isDesktopToolbarLoading = (updateTravellers === null || updateTravellers === void 0 ? void 0 : updateTravellers.loading) || loading || saveOrderServicesResult.loading || servicesLoading;
    const hasLinkedTravellerError = React.useMemo(() => { var _a; return (_a = formState === null || formState === void 0 ? void 0 : formState.errors['passengers']) === null || _a === void 0 ? void 0 : _a.some((passenger) => !!(passenger === null || passenger === void 0 ? void 0 : passenger.linkedTraveller)); }, [formState === null || formState === void 0 ? void 0 : formState.errors]);
    let isDisabledButton = disableBooking || hasLinkedTravellerError;
    if (!isTravellersDataFilled && !isNew) {
        isDisabledButton = isDisabledButton || (!handleSubmit && !isFareLockSelected);
    }
    const isFooterMobileDisabled = disableBooking || loading || saveOrderServicesResult.loading || servicesLoading || hasLinkedTravellerError;
    return (React.createElement(React.Fragment, null,
        ffpCompleteOrderResult.loading && React.createElement(SimpleLoader, null),
        showToolbar && (React.createElement(React.Fragment, null,
            React.createElement(Warning, { open: showWarning, redirectURL: servicesRedirectURL, onClose: closeWarning }),
            React.createElement(InsuranceUpsaleModal, { insurance: insuranceUpsaleModal.priorityInsurance, isOpen: insuranceUpsaleModal.isUpsaleOpen, onAgree: () => insuranceUpsaleModal.onAgree(savePriorityInsurance), onDisagree: () => insuranceUpsaleModal.onDisagree(onContinuePurchasePredictionService) }),
            React.createElement(AIPurchasePredictionService, { order: order, service: aiPurchasePredictionService.service, onAgree: service => aiPurchasePredictionService.onAgree(service, saveAdditionalService), onChange: payload => aiPurchasePredictionService.onChange(payload, onChangeStep), onDisagree: onAiPurchaseDisagree }),
            React.createElement(CustomerContactsModal, { initialValues: customerValues, onContactsSubmit: saveClientInfoAndUpdateOrder, isLoading: isClientInfoSaving, isOpen: isContactsModalOpen, onClose: closeContactsModal }),
            React.createElement(InsuranceModalConfirmINN, { isOpen: isInsuranceModalOpen, onClose: closeInsuranceModal, onSubmit: saveInsuranceAndUpdateOrder, isSubmitLoading: isSaveOrderServicesLoading, passengers: passengersToConfirmIIN }),
            React.createElement(MediaQuery, { minWidth: MOBILE_MIN_WIDTH },
                React.createElement(Toolbar, { isLoading: isDesktopToolbarLoading, actionsRight: isBooked, hideNextButton: !hasPriceOrMilesToPay && isTravellersDataFilled, totalPrice: totalPriceToPay, priceInMiles: !isConfirmedOrCancelled && priceInMilesForFlight, onButtonClick: isBookedOrConfirmed
                        ? () => insuranceUpsaleModal.onContinue(onContinuePurchasePredictionService)
                        : null, miles: !isConfirmedOrCancelled && bonusForFlight, buttonDisabled: isDisabledButton, additional: React.createElement(React.Fragment, null,
                        isBooked && totalInsurancePrice && totalInsurancePrice.amount && (React.createElement("div", { className: checkoutTheme.gdsTotalPrice },
                            t('Insurance'),
                            ": ",
                            React.createElement(MoneyComponent, { money: totalInsurancePrice }))),
                        React.createElement("div", null,
                            showConsents && isNew && React.createElement(PassengersToolbarCheckbox, null),
                            React.createElement(RenderPassengersToolbarAdditionalOverride, { order: order }))) })),
            React.createElement(MediaQuery, { maxWidth: MOBILE_MIN_WIDTH },
                ((showConsents && isNew) || RenderPassengersToolbarAdditionalOverride !== NULL_COMPONENT) && (React.createElement("div", { className: toolbarTheme.toolbarCheckbox__wrapper },
                    showConsents && isNew && React.createElement(PassengersToolbarCheckbox, null),
                    React.createElement(RenderPassengersToolbarAdditionalOverride, { order: order }))),
                React.createElement(FooterMobile, { onBack: goBack, disabled: isFooterMobileDisabled, onContinue: isBookedOrConfirmed && hasPriceOrMilesToPay
                        ? () => insuranceUpsaleModal.onContinue(onContinuePurchasePredictionService)
                        : isBookedOrConfirmed
                            ? undefined
                            : null }))))));
};
export default PassengersToolbarWithoutForm;
